import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Users from "@/pages/Users";
import Dashboard from "@/pages/Dashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import Machines from "@/pages/Machines";
import Materials from "@/pages/Materials";
import Bop from "@/pages/Bop";
import Manpower from "@/pages/Manpower";
import PartDetail from "@/pages/PartDetail";
import BusinessProposal from "@/pages/BusinessProposal";
import ProposalReport from "@/pages/ProposalReport";
import CustomerQuote from "@/pages/CustomerQuote";
import Vendors from "@/pages/Vendors";
import Tasks from "@/pages/Tasks";
import Layout from "@/components/Layout";

function ProtectedRoute({ children, adminOnly = false, blockProjectManager = false }) {
    const { user, loading } = useAuth();
    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono">Loading…</div>;
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.must_change_password && window.location.pathname !== "/change-password") {
        return <Navigate to="/change-password?required=1" replace />;
    }
    if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
    // Project managers are locked to the Tasks module
    if (user.role === "project_manager" && blockProjectManager) return <Navigate to="/tasks" replace />;
    return <Layout>{children}</Layout>;
}

// Landing route — project managers land on /tasks, everyone else on /dashboard
function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={user.role === "project_manager" ? "/tasks" : "/dashboard"} replace />;
}

function AppRouter() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<SetPassword mode="reset" />} />
            <Route path="/activate" element={<SetPassword mode="activate" />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={<ProtectedRoute blockProjectManager><Dashboard /></ProtectedRoute>} />
            <Route path="/sales" element={<ProtectedRoute blockProjectManager><SalesDashboard /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
            <Route path="/master/machines" element={<ProtectedRoute adminOnly><Machines /></ProtectedRoute>} />
            <Route path="/master/materials" element={<ProtectedRoute adminOnly><Materials /></ProtectedRoute>} />
            <Route path="/master/bop" element={<ProtectedRoute adminOnly><Bop /></ProtectedRoute>} />
            <Route path="/master/manpower" element={<ProtectedRoute adminOnly><Manpower /></ProtectedRoute>} />
            <Route path="/parts/:partId" element={<Navigate to="costing" replace />} />
            <Route path="/parts/:partId/costing" element={<ProtectedRoute blockProjectManager><PartDetail /></ProtectedRoute>} />
            <Route path="/parts/:partId/proposal" element={<ProtectedRoute blockProjectManager><ProposalReport /></ProtectedRoute>} />
            <Route path="/parts/:partId/roi" element={<ProtectedRoute blockProjectManager><ProposalReport /></ProtectedRoute>} />
            <Route path="/parts/:partId/report" element={<ProtectedRoute blockProjectManager><ProposalReport /></ProtectedRoute>} />
            <Route path="/parts/:partId/customer-quote" element={<ProtectedRoute blockProjectManager><CustomerQuote /></ProtectedRoute>} />
            <Route path="/master/vendors" element={<ProtectedRoute adminOnly><Vendors /></ProtectedRoute>} />
            <Route path="/parts/:partId/proposal-legacy" element={<ProtectedRoute blockProjectManager><BusinessProposal /></ProtectedRoute>} />
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <AppRouter />
                    <Toaster richColors position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
